import { createApp } from "vue"
import { createPinia } from "pinia"
import piniaPluginPersistedstate from "pinia-plugin-persistedstate"

import Root from "./Root.vue"
import router from "./router"
import BaseLayout from "./components/Base/BaseLayout.vue"
import { Capacitor } from "@capacitor/core"
import { sqlite } from "@/helpers/sqlite"
import { JeepSqlite } from "jeep-sqlite/dist/components/jeep-sqlite"
import { defineCustomElements as pwaElements } from "@ionic/pwa-elements/loader"
import { IonicVue } from "@ionic/vue"
import { useDatabaseStore } from "@/stores/database"
import * as Sentry from "@sentry/capacitor"
import * as SentryVue from "@sentry/vue"

/* Core CSS required for Ionic components to work properly */
import "@ionic/vue/css/core.css"

/* Basic CSS for apps built with Ionic */
import "@ionic/vue/css/normalize.css"
import "@ionic/vue/css/structure.css"
import "@ionic/vue/css/typography.css"

/* Optional CSS utils that can be commented out */
import "@ionic/vue/css/padding.css"
import "@ionic/vue/css/float-elements.css"
import "@ionic/vue/css/text-alignment.css"
import "@ionic/vue/css/text-transformation.css"
import "@ionic/vue/css/flex-utils.css"
import "@ionic/vue/css/display.css"

/* trackiwi Theme */
import "./theme/index.css"

const pinia = createPinia()
pinia.use(piniaPluginPersistedstate)

const app = createApp(Root)
	//.use(IonicVue, { mode: "ios" })
	.use(IonicVue)
	.use(router)
	.use(pinia)

if (import.meta.env.VITE_BUILD_MODE === "production") {
	Sentry.init(
		{
			app,
			dsn: "https://91f3d9abba0cf6218259909f94eda3c0@o4509598002839552.ingest.de.sentry.io/4509598226710608",
			integrations: [Sentry.browserTracingIntegration()],
		},
		SentryVue.init,
	)
} else {
	console.log("❌  SENTRY DISABLED")
}

const runWebSetup = async () => {
	customElements.define("jeep-sqlite", JeepSqlite)
	pwaElements(window)
	const jeepEl = document.createElement("jeep-sqlite")
	jeepEl.setAttribute("autoSave", "false")
	document.body.appendChild(jeepEl)
	await customElements.whenDefined("jeep-sqlite")
	await sqlite.initWebStore()

	if ("serviceWorker" in navigator) {
		navigator.serviceWorker
			.register("/sw.js")
			.then((registration) => {
				console.log("🚀 registered sw.js", registration.scope)
			})
			.catch((error) => {
				console.error("❌ registering sw.js failed", error)
			})
	}

	await mountApp()
}

const runNativeSetup = () => {
	mountApp()
}

const mountApp = async () => {
	const database = useDatabaseStore()
	try {
		await database.init()
	} catch (error) {
		// never block the app start on the database, a missing route cache is better than a blank screen
		console.error("🛢️ database initialisation failed", error)
		Sentry.captureException(error)
	}

	app.component("base-layout", BaseLayout)
	app.mount("#app")
}

const startSetup = async () => {
	await router.isReady()
	const platform = Capacitor.getPlatform()

	if (platform !== "web") {
		runNativeSetup()
	} else {
		if (document.readyState === "loading") {
			document.addEventListener("DOMContentLoaded", runWebSetup)
		} else {
			runWebSetup()
		}
	}
}

startSetup()
